/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import type { ChatAppResponse, ChatMessageModel, ChatSessionModel } from "@/api/models"

import {
  createSessionApi,
  getMessagesApi,
  listSessionsApi,
  listSessionsBatchApi,
  saveWorkflowRunMessageApi,
} from "@/api/api"
import { SettingsService } from "@/services/settings-service"
export class ChatService {
  private static instance: ChatService
  private openSessions: { [key: string]: ChatSessionModel } = {}
  // HERMES (B6): hosted-agent binding per sessionType. While set, every
  // session CREATED for that sessionType is bound to the agent server-side
  // (`hosted_agent_id` on /create-session). The binding never touches
  // existing sessions — the server stamps visibility at creation only.
  private hostedAgentBindings: { [sessionType: string]: string } = {}
  private settingsService: SettingsService

  public static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService()
    }
    return ChatService.instance
  }

  private constructor() {
    this.settingsService = SettingsService.getInstance()
  }

  removeSession(sessionId: string): void {
    for (const key in this.openSessions) {
      if (this.openSessions[key]?.id === sessionId) {
        delete this.openSessions[key]
      }
    }
  }

  removeSessions(sessionIds: string[]): void {
    Object.keys(this.openSessions).forEach((key) => {
      if (!this.openSessions[key]?.id || !sessionIds.includes(this.openSessions[key]!.id!)) {
        return
      }

      delete this.openSessions[key]
    })
  }

  closeSession(sessionType: string): void {
    const session = this.getOpenSession(sessionType)
    if (session !== undefined) {
      delete this.openSessions[sessionType]
    }
  }

  openSession(sessionType: string, session: ChatSessionModel): void {
    this.openSessions[sessionType] = session
  }

  /** HERMES (B6): bind future session creations for this sessionType to a hosted agent. */
  bindHostedAgent(sessionType: string, hostedAgentId: string): void {
    this.hostedAgentBindings[sessionType] = hostedAgentId
  }

  /**
   * Clears the hosted-agent binding. When `hostedAgentId` is given, only a
   * binding to that exact agent is cleared — so an unmount cleanup can't
   * clobber a newer binding set by the next agent's view.
   */
  unbindHostedAgent(sessionType: string, hostedAgentId?: string): void {
    if (hostedAgentId && this.hostedAgentBindings[sessionType] !== hostedAgentId) {
      return
    }
    delete this.hostedAgentBindings[sessionType]
  }

  getHostedAgentBinding(sessionType: string): string | undefined {
    return this.hostedAgentBindings[sessionType]
  }
  /* eslint-disable */
  async createSession(
    sessionType: string,
    title: string = "",
    customTitle?: string,
    sensitiveData?: boolean,
    existingSessionId?: string,
    projectId?: string,
    sourceSessionId?: string,
    sourceMessageId?: string,
    iconType?: string
  ): Promise<ChatSessionModel> {
    // Use the existing session ID if provided, otherwise it will be generated on the server
    const data: ChatSessionModel = await createSessionApi(
      title,
      sessionType,
      customTitle,
      sensitiveData,
      existingSessionId,
      this.settingsService.token,
      projectId,
      sourceSessionId,
      sourceMessageId,
      iconType,
      this.getHostedAgentBinding(sessionType)
    )
    this.openSessions[sessionType] = data
    return data
  }

  async listSessions(
    sessionType: string,
    projectId?: string,
    signal?: AbortSignal
  ): Promise<ChatSessionModel[] | undefined> {
    return listSessionsApi(sessionType, this.settingsService.token, projectId, signal)
  }

  async listSessionsBatch(
    sessionTypes: string[],
    signal?: AbortSignal
  ): Promise<ChatSessionModel[] | undefined> {
    return listSessionsBatchApi(sessionTypes, this.settingsService.token, signal)
  }

  async getMessages(sessionId: string): Promise<ChatMessageModel[]> {
    return getMessagesApi(sessionId, this.settingsService.token)
  }

  async saveWorkflowRunMessage(
    sessionId: string,
    question: string,
    response: ChatAppResponse
  ): Promise<{ success: boolean; message_id: string }> {
    return saveWorkflowRunMessageApi(sessionId, question, response, this.settingsService.token)
  }

  updateSessionTitle(sessionId: string, title: string): void {
    Object.keys(this.openSessions).forEach((key) => {
      const session = this.openSessions[key]
      if (session?.id === sessionId) {
        this.openSessions[key] = { ...session, title }
      }
    })
  }

  getOpenSession(sessionType: string): ChatSessionModel | undefined {
    if (sessionType in this.openSessions) {
      return this.openSessions[sessionType]
    }
    return undefined
  }

  getSessionById(sessionId: string): ChatSessionModel | undefined {
    return Object.values(this.openSessions).find((session) => session?.id === sessionId)
  }
}
